"use client";

import { useEffect, useState } from "react";
import { analytics } from "@/lib/analytics/core";

const SHOW_POPUP = process.env.NEXT_PUBLIC_SHOW_CONSENT_POPUP === "true";

const CONSENT_KEY = "cookie-consent";
const CONSENT_EVENT = "cookie-consent-changed";

type ConsentStatus = "accepted" | "rejected" | null;

function getStoredConsent(): ConsentStatus {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    if (v === "accepted" || v === "rejected") return v;
    return null;
  } catch {
    return null;
  }
}

function setStoredConsent(value: "accepted" | "rejected") {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    /* private browsing — swallow */
  }
  window.dispatchEvent(new Event(CONSENT_EVENT));
}

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!SHOW_POPUP) return;
    if (getStoredConsent() !== null) return;
    setVisible(true);
  }, []);

  if (!visible) return null;

  const accept = () => {
    setStoredConsent("accepted");
    setVisible(false);
    analytics.track("cookie_consent", { action: "accept" });
  };

  const reject = () => {
    setStoredConsent("rejected");
    setVisible(false);
    analytics.track("cookie_consent", { action: "reject" });
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-9999 animate-[slideUp_0.35s_ease-out]"
    >
      <div className="mx-auto max-w-5xl px-4 pb-4 sm:px-6">
        <div className="rounded-2xl border border-(--border-light) bg-white/95 shadow-2xl backdrop-blur-md sm:rounded-2xl dark:bg-[#1a1f25]/95">
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:gap-6 sm:p-6">
            <div className="flex-1 text-sm leading-relaxed text-(--text-secondary)">
              We use cookies and analytics tools to understand how visitors
              interact with our site so we can improve the experience. By
              accepting, you consent to the use of analytics cookies.{" "}
              <a
                href="/privacy-policy"
                className="font-medium text-medease-teal underline underline-offset-2 transition-colors hover:text-primary"
              >
                Privacy&nbsp;Policy
              </a>
            </div>

            <div className="flex shrink-0 gap-3">
              <button
                onClick={reject}
                className="cursor-pointer rounded-lg border border-(--border-default) bg-white px-5 py-2.5 text-sm font-semibold text-(--text-secondary) transition-all hover:border-(--border-light) hover:bg-(--surface-hover) dark:bg-[#1a1f25]"
              >
                Reject
              </button>
              <button
                onClick={accept}
                className="cursor-pointer rounded-lg bg-medease-teal px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-medease-teal-light"
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
